/**
 * CommentContent - Renders comment text with @mentions, #hashtags, 📍 locations as links.
 * AuraPill allows giving aura to mentions, hashtags, and locations in comments.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { parseContentWithHashtagsMentionsAndLocation } from './PostContent';
import AuraPill from './AuraPill';

function CommentContent({ comment }) {
  const content = comment.comment_text;
  const mentions = comment.mentions || [];
  const hashtags = comment.hashtags || [];
  const location = comment.location || null;
  const segments = parseContentWithHashtagsMentionsAndLocation(content, mentions, location);
  const hashtagMap = new Map((hashtags || []).map((h) => [h.hashtag?.toLowerCase(), h]));

  if (!content) return null;

  return (
    <span className="text-silver whitespace-pre-wrap break-words">
      {segments.map((seg, i) =>
        seg.type === 'mention' ? (
          <span key={i} className="inline-flex items-center gap-1">
            <Link
              to={`/profile/${encodeURIComponent(seg.value)}`}
              className="text-ether hover:underline"
            >
              @{seg.value}
            </Link>
            {seg.user && (
              <AuraPill
                targetId={seg.user.user_id}
                targetType="user"
                commentId={comment.comment_id}
                auraCount={seg.user.aura_count ?? 0}
              />
            )}
          </span>
        ) : seg.type === 'hashtag' ? (
          <span key={i} className="inline-flex items-center gap-1">
            <Link
              to={`/?hashtag=${encodeURIComponent(seg.value)}`}
              className="text-ether hover:underline"
            >
              #{seg.value}
            </Link>
            {hashtagMap.get(seg.value?.toLowerCase()) && (
              <AuraPill
                targetId={hashtagMap.get(seg.value?.toLowerCase()).hashtag_id}
                targetType="hashtag"
                commentId={comment.comment_id}
                auraCount={hashtagMap.get(seg.value?.toLowerCase()).aura_count ?? 0}
              />
            )}
          </span>
        ) : seg.type === 'location' ? (
          <span key={i} className="inline-flex items-center gap-1">
            {seg.location?.location_id ? (
              <>
                <Link
                  to={`/?location_id=${seg.location.location_id}`}
                  className="text-mist hover:underline"
                >
                  📍 {seg.value}
                </Link>
                <AuraPill
                  targetId={seg.location.location_id}
                  targetType="location"
                  commentId={comment.comment_id}
                  auraCount={seg.location.aura_count ?? 0}
                />
              </>
            ) : (
              <span className="text-mist">📍 {seg.value}</span>
            )}
          </span>
        ) : (
          <span key={i}>{seg.value}</span>
        )
      )}
    </span>
  );
}

export default CommentContent;
